// TODO: Write tests for this file

import React from "react";
import {
  Container,
  GKTable,
  GKButton,
  GKCollapse,
  GKTooltip,
  GKIcon,
  GKIconName,
} from "@granular/gds-ux-components";
import { LiveProvider, LiveEditor, LiveError, LivePreview } from "react-live";
import theme from "prism-react-renderer/themes/vsDark";

import { DOCS_ID } from "./constants";
import {
  FederatedModulesDocsPageUsageBlock,
  isUsageBlockWithDescriptionComponent,
  isUsageBlockWithDescriptionJsx,
} from "./helpers";
import {
  formatBoolean,
  formatCode,
  formatPropName,
  formatPropType,
  parsePropsFile,
} from "./DocsPage.helpers";

export type FederatedModulesDocsPageProps = {
  docgenResult: any;
  propsFile: string;
  usageBlocks: FederatedModulesDocsPageUsageBlock[];
};

export const FederatedModulesDocsPage: React.FC<FederatedModulesDocsPageProps> =
  (props) => {
    const name = props.docgenResult.displayName;
    const parsedProps = parsePropsFile(props.propsFile);

    const [isOpenProps, setIsOpenProps] = React.useState(false);
    const [isOpenUsage, setIsOpenUsage] = React.useState(false);
    const [isUsageBgTransparent, setIsUsageBgTransparent] =
      React.useState(false);

    const propsContent =
      parsedProps.length === 0 ? (
        <p>Component has no props.</p>
      ) : (
        <GKTable hover responsive striped>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Description</th>
              <th>Required?</th>
            </tr>
          </thead>
          <tbody>
            {parsedProps.map((parsedProp) => {
              return (
                <tr
                  key={parsedProp.propName}
                  className={parsedProp.isPropDeprecated ? "deprecated" : ""}
                >
                  <td>{formatPropName(parsedProp)}</td>
                  <td>{formatPropType(parsedProp.propType)}</td>
                  <td>{parsedProp.propDescription}</td>
                  <td>{formatBoolean(parsedProp.isPropRequired)}</td>
                </tr>
              );
            })}
          </tbody>
        </GKTable>
      );

    // TODO: Probably want to process this as markdown.
    const unformattedDescription: string | undefined =
      props.docgenResult.description;
    const formattedDescription = unformattedDescription ? (
      unformattedDescription.split("\n").map((str) => (
        <>
          {str}
          <br />
        </>
      ))
    ) : (
      <>No description provided.</>
    );

    return (
      <Container className="my-5">
        <h2 className="overflow-auto">{name}</h2>
        <p>{formattedDescription}</p>
        <div className="d-flex justify-content-between align-items-center pt-3">
          <h3>Props</h3>
          <GKButton
            id={`${DOCS_ID}--toggle-props-${name}`}
            onClick={() => setIsOpenProps(!isOpenProps)}
          >
            <GKIcon name={GKIconName.Code} />
          </GKButton>
          <GKTooltip target={`${DOCS_ID}--toggle-props-${name}`}>
            {isOpenProps ? "Hide Code" : "Show Code"}
          </GKTooltip>
        </div>
        <div className="mt-3">
          {propsContent}
          <GKCollapse isOpen={isOpenProps}>
            <LiveProvider
              code={formatCode(props.propsFile)}
              theme={theme}
              language="typescript"
            >
              <LiveEditor disabled />
            </LiveProvider>
          </GKCollapse>
        </div>
        <div className="d-flex justify-content-between align-items-center pt-3">
          <h3>Usage</h3>
          <div>
            <GKButton
              id={`${DOCS_ID}--toggle-usage-bg-${name}`}
              onClick={() => setIsUsageBgTransparent(!isUsageBgTransparent)}
              className="mr-2"
            >
              <GKIcon name={GKIconName.Preview} />
            </GKButton>
            <GKTooltip target={`${DOCS_ID}--toggle-usage-bg-${name}`}>
              Toggle Transparent Background
            </GKTooltip>
            <GKButton
              id={`${DOCS_ID}--toggle-usage-${name}`}
              onClick={() => setIsOpenUsage(!isOpenUsage)}
            >
              <GKIcon name={GKIconName.Code} />
            </GKButton>
            <GKTooltip target={`${DOCS_ID}--toggle-usage-${name}`}>
              {isOpenUsage ? "Hide Code" : "Show Code"}
            </GKTooltip>
          </div>
        </div>
        {props.usageBlocks.map((usageBlock) => {
          let Description: React.ComponentType;
          if (isUsageBlockWithDescriptionComponent(usageBlock)) {
            Description = usageBlock.Description;
          } else if (isUsageBlockWithDescriptionJsx(usageBlock)) {
            Description = () => usageBlock.description;
          } else {
            Description = () => null;
          }
          return (
            <div className="mt-3" key={usageBlock.code}>
              <Description />
              <LiveProvider
                code={formatCode(usageBlock.code)}
                // rome-ignore lint/a11y/noHeaderScope: TODO fix this later
                scope={usageBlock.codeDependencies}
                theme={theme}
                language="tsx"
              >
                <LivePreview
                  className={`d-flex justify-content-center align-items-center mt-3 p-3 ${
                    isUsageBgTransparent ? "bg-transparent" : "bg-white"
                  }`}
                />
                <GKCollapse isOpen={isOpenUsage}>
                  <LiveEditor />
                  <LiveError className="bg-danger text-white p-3" />
                </GKCollapse>
              </LiveProvider>
            </div>
          );
        })}
      </Container>
    );
  };
